import { pb } from './Pocketbase'
import type { IBrand, INote, IPerfumer, ICountry, IPrice } from '../types'
import { normalizeString, generateSlug } from '../utils/stringUtils'

// Re-export types for convenience
export type { IBrand, INote, IPerfumer, ICountry, IPrice }

// Generic result type for paginated queries
interface PaginatedResult<T> {
  items: T[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

// BRANDS
export const getBrands = async (
  page: number = 1,
  perPage: number = 10,
  search?: string,
  sort: string = 'name'
): Promise<PaginatedResult<IBrand>> => {
  const filter = search ? pb.filter('name ~ {:search}', { search }) : ''
  const result = await pb.collection('brands').getList<IBrand>(page, perPage, {
    sort,
    filter,
  })
  return result
}

export const getBrandsCount = async (): Promise<number> => {
  const result = await pb.collection('brands').getList(1, 1)
  return result.totalItems
}

export const createBrand = async (name: string): Promise<IBrand> => {
  const record = await pb.collection('brands').create<IBrand>({
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
  return record
}

export const updateBrand = async (id: string, name: string): Promise<void> => {
  await pb.collection('brands').update(id, {
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
}

export const deleteBrand = async (id: string): Promise<void> => {
  await pb.collection('brands').delete(id)
}

// NOTES
export const getNotes = async (
  page: number = 1,
  perPage: number = 10,
  search?: string,
  sort: string = 'name'
): Promise<PaginatedResult<INote>> => {
  const filter = search ? pb.filter('name ~ {:search}', { search }) : ''
  const result = await pb.collection('notes').getList<INote>(page, perPage, {
    sort,
    filter,
  })
  return result
}

export const getNotesCount = async (): Promise<number> => {
  const result = await pb.collection('notes').getList(1, 1)
  return result.totalItems
}

export const createNote = async (name: string): Promise<INote> => {
  const record = await pb.collection('notes').create<INote>({
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
  return record
}

export const updateNote = async (id: string, name: string): Promise<void> => {
  await pb.collection('notes').update(id, {
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
}

export const deleteNote = async (id: string): Promise<void> => {
  await pb.collection('notes').delete(id)
}

export const updateMetadataImage = async (
  collectionName: string,
  id: string,
  imageFile: File
): Promise<void> => {
  const formData = new FormData()
  formData.append('image', imageFile)
  await pb.collection(collectionName).update(id, formData)
}

// PERFUMERS
export const getPerfumers = async (
  page: number = 1,
  perPage: number = 10,
  search?: string
): Promise<PaginatedResult<IPerfumer>> => {
  const filter = search ? pb.filter('name ~ {:search}', { search }) : ''
  const result = await pb.collection('perfumers').getList<IPerfumer>(page, perPage, {
    sort: 'name',
    filter,
  })
  return result
}

export const getPerfumersCount = async (): Promise<number> => {
  const result = await pb.collection('perfumers').getList(1, 1)
  return result.totalItems
}

export const createPerfumer = async (name: string): Promise<IPerfumer> => {
  const record = await pb.collection('perfumers').create<IPerfumer>({
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
  return record
}

export const updatePerfumer = async (id: string, name: string): Promise<void> => {
  await pb.collection('perfumers').update(id, {
    name,
    normalized_name: normalizeString(name),
    slug: generateSlug(name)
  })
}

export const deletePerfumer = async (id: string): Promise<void> => {
  await pb.collection('perfumers').delete(id)
}

// COUNTRIES
export const getCountries = async (
  page: number = 1,
  perPage: number = 10,
  search?: string
): Promise<PaginatedResult<ICountry>> => {
  const filter = search ? pb.filter('name ~ {:search}', { search }) : ''
  const result = await pb.collection('countries').getList<ICountry>(page, perPage, {
    sort: 'name',
    filter,
  })
  return result
}

export const getCountriesCount = async (): Promise<number> => {
  const result = await pb.collection('countries').getList(1, 1)
  return result.totalItems
}

export const createCountry = async (name: string): Promise<ICountry> => {
  const record = await pb.collection('countries').create<ICountry>({
    name,
    normalized_name: normalizeString(name)
  })
  return record
}

export const updateCountry = async (id: string, name: string): Promise<void> => {
  await pb.collection('countries').update(id, {
    name,
    normalized_name: normalizeString(name)
  })
}

export const deleteCountry = async (id: string): Promise<void> => {
  await pb.collection('countries').delete(id)
}

// GET ALL FUNCTIONS (for forms/dropdowns)
export const getAllBrands = async (): Promise<IBrand[]> => {
  const records = await pb.collection('brands').getFullList<IBrand>({
    sort: 'name',
  })
  return records
}

export const getAllNotes = async (): Promise<INote[]> => {
  const records = await pb.collection('notes').getFullList<INote>({
    sort: 'name',
  })
  return records
}

export const getAllPerfumers = async (): Promise<IPerfumer[]> => {
  const records = await pb.collection('perfumers').getFullList<IPerfumer>({
    sort: 'name',
  })
  return records
}

export const getAllCountries = async (): Promise<ICountry[]> => {
  const records = await pb.collection('countries').getFullList<ICountry>({
    sort: 'name',
  })
  return records
}

export const getAllPrices = async (): Promise<IPrice[]> => {
  const records = await pb.collection('prices').getFullList<IPrice>({
    sort: 'name',
  })
  return records
}

// PRICES (CRUD operations for admin)
export const getPrices = async (
  page: number = 1,
  perPage: number = 10
): Promise<PaginatedResult<IPrice>> => {
  const result = await pb.collection('prices').getList<IPrice>(page, perPage, {
    sort: 'name',
  })
  return result
}

export const getPricesCount = async (): Promise<number> => {
  const result = await pb.collection('prices').getList(1, 1)
  return result.totalItems
}

export const createPrice = async (name: string): Promise<IPrice> => {
  const record = await pb.collection('prices').create<IPrice>({
    name,
    normalized_name: normalizeString(name)
  })
  return record
}

export const updatePrice = async (id: string, name: string): Promise<void> => {
  await pb.collection('prices').update(id, {
    name,
    normalized_name: normalizeString(name)
  })
}


export interface IApprovedSubmissionName {
  id: string
  name: string
}

export const getAllApprovedSubmissionNames = async (): Promise<IApprovedSubmissionName[]> => {
  const records = await pb.collection('approved_submissions').getFullList<IApprovedSubmissionName>({
    sort: 'name',
    fields: 'id,name'
  })
  return records
}
